// 편집기 전용 레이아웃 그리드 — 슬라이드에 저장되지 않고, 이 브라우저에만 기억한다.
// 그리드가 보일 때 Shift+드래그 이동은 가장 가까운 선에 붙는다(안 보이면 기존처럼 축 고정 이동).
/** 열·행 공통: 개수, 사이 간격, 바깥 여백(대지 px). */
export type LayoutGridBand = { on: boolean; count: number; gutter: number; margin: number };
export type NoticeLayoutGrid = {
  visible: boolean;
  columns: LayoutGridBand;
  rows: LayoutGridBand;
  square: { on: boolean; size: number };
};
export const LAYOUT_GRID_STORAGE_KEY = 'bricklit-notice-layout-grid-v1';
// FHD 기준: 열 80 + 110 × 12 + 40 × 11 = 1920, 행 80 + 120 × 6 + 40 × 5 = 1080.
export const DEFAULT_LAYOUT_GRID: NoticeLayoutGrid = {
  visible: false,
  columns: { on: true, count: 12, gutter: 40, margin: 80 },
  rows: { on: false, count: 6, gutter: 40, margin: 80 },
  square: { on: false, size: 40 },
};

const int = (value: unknown, min: number, max: number, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(min, Math.min(max, Math.round(value))) : fallback;
const band = (value: Partial<LayoutGridBand> | undefined, d: LayoutGridBand): LayoutGridBand => ({
  on: typeof value?.on === 'boolean' ? value.on : d.on, count: int(value?.count, 1, 24, d.count),
  gutter: int(value?.gutter, 0, 2000, d.gutter), margin: int(value?.margin, 0, 4000, d.margin),
});
/** 저장값·입력값을 범위 안으로. 항목이 없으면(예: 행이 생기기 전 저장값) 기본값으로 채운다. */
export function clampLayoutGrid(value: Partial<NoticeLayoutGrid> | null | undefined): NoticeLayoutGrid {
  const d = DEFAULT_LAYOUT_GRID, s = value?.square;
  return {
    visible: typeof value?.visible === 'boolean' ? value.visible : d.visible,
    columns: band(value?.columns, d.columns),
    rows: band(value?.rows, d.rows),
    square: { on: typeof s?.on === 'boolean' ? s.on : d.square.on, size: int(s?.size, 4, 2000, d.square.size) },
  };
}
export function readLayoutGrid(): NoticeLayoutGrid {
  try { return clampLayoutGrid(JSON.parse(localStorage.getItem(LAYOUT_GRID_STORAGE_KEY) ?? 'null') as Partial<NoticeLayoutGrid> | null); }
  catch { return DEFAULT_LAYOUT_GRID; }
}
export function saveLayoutGrid(grid: NoticeLayoutGrid): void {
  try { localStorage.setItem(LAYOUT_GRID_STORAGE_KEY, JSON.stringify(grid)); } catch { /* 저장소가 막혀도 편집은 된다. */ }
}

/** 띠의 시작·길이(대지 좌표). 여백·간격이 너무 커서 띠 길이가 0 이하면 빈 배열. */
export function layoutBands({ on, count, gutter, margin }: LayoutGridBand, length: number): Array<{ start: number; size: number }> {
  const size = (length - margin * 2 - gutter * (count - 1)) / count;
  if (!on || size <= 0) return [];
  return Array.from({ length: count }, (_, i) => ({ start: margin + i * (size + gutter), size }));
}
/** 붙을 수 있는 선(대지 좌표): 대지 가장자리, 열·행 경계, 정사각 격자선. */
export function layoutGridLines(grid: NoticeLayoutGrid, width: number, height: number): { x: number[]; y: number[] } {
  const x = [0, width], y = [0, height];
  for (const column of layoutBands(grid.columns, width)) x.push(column.start, column.start + column.size);
  for (const row of layoutBands(grid.rows, height)) y.push(row.start, row.start + row.size);
  if (grid.square.on) {
    for (let at = grid.square.size; at < width; at += grid.square.size) x.push(at);
    for (let at = grid.square.size; at < height; at += grid.square.size) y.push(at);
  }
  const unique = (lines: number[]) => [...new Set(lines.map(v => Math.round(v * 100) / 100))].sort((a, b) => a - b);
  return { x: unique(x), y: unique(y) };
}
/** 상자의 앞·뒤 가장자리 중 선에 더 가까운 쪽을 그 선에 붙인 시작 위치. */
export function snapToLines(start: number, size: number, lines: readonly number[]): number {
  let best = start, distance = Infinity;
  for (const line of lines) for (const candidate of [line, line - size]) {
    const d = Math.abs(candidate - start);
    if (d < distance) { distance = d; best = candidate; }
  }
  return best;
}
